import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import '../components/common/Toast.css';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: number;
  message: string;
  type: ToastType;
  durationMs: number;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType, durationMs?: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const DEFAULT_DURATION_MS = 4000;

let nextId = 0;

const ICONS: Record<ToastType, string> = {
  success: '✓',
  error: '✕',
  warning: '⚠',
  info: 'i',
};

const ToastItem: React.FC<{ toast: Toast; onRemove: (id: number) => void }> = ({ toast, onRemove }) => (
  <div className={`toast toast-${toast.type}`} onClick={() => onRemove(toast.id)}>
    <span className="toast-icon">{ICONS[toast.type]}</span>
    <span className="toast-message">{toast.message}</span>
    <button className="toast-close" onClick={(e) => { e.stopPropagation(); onRemove(toast.id); }}>✕</button>
    <div className="toast-progress" style={{ animationDuration: `${toast.durationMs}ms` }} />
  </div>
);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const showToast = useCallback((message: string, type: ToastType = 'success', durationMs: number = DEFAULT_DURATION_MS) => {
    const id = nextId++;
    setToasts(prev => [...prev, { id, message, type, durationMs }]);
    setTimeout(() => removeToast(id), durationMs);
  }, [removeToast]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="toast-container">
        {toasts.map(t => (
          <ToastItem key={t.id} toast={t} onRemove={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within ToastProvider');
  return context;
};
